import './playlistItem.scss';
import React, { useEffect, useState, type ReactNode } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMiwiStore, playlistStore } from '../../store';
import Helpers from '../../js/helpers';
import Server from '../../js/config/_server';
import { RESPONSE, OVERRIDE_SISSION_DURATION } from '../../js/_constants';
import Config from '../../js/config/_global';
import { apiPatch } from '../../js/api';
import {
    type Uuid,
    type PlaylistItem as PlaylistItemType,
} from '../../api/types/index';
import ItemCard, { type ActionButtonType } from '../ItemCard/ItemCard';
import Loader from '../loader/Loader';
import { Slider } from '@mui/material';
import { DeviceType } from '@org-avp/device-models';
import AiComplianceIcon from '../aiComplianceIcon/aiComplianceIcon';

type PlaylistItemProps = {
    setIsEditingPlaylist: any;
    item: PlaylistItemType;
    deleteItemRequest: (param: PlaylistItemType) => void;
    playlistId: Uuid;
    disabled?: boolean;
    isEditable: boolean;
    onMove: any;
    index: any;
    itemsLength: any,
};

const PlaylistItem = ({
    setIsEditingPlaylist,
    item,
    deleteItemRequest,
    playlistId,
    disabled,
    isEditable,
    onMove,
    index,
    itemsLength,
}: PlaylistItemProps) => {
    const { t } = useTranslation();
    const { overrideMediaUrl, overrideDeviceId, overrideDeviceType, overrideSessionId,
        setAsset2D, controlMedia2D, setAsset3D, setOverride , setOverrideMediaUrl, setOverrideMedia } = useMiwiStore();
    const { getPlaylist } = playlistStore();
    const [playlistItem, setPlaylistItem] = useState(item);
    const [checkExpiration, setExp] = useState('');
    const [duration, setDuration] = useState(item.duration);
    const [isUpdating, setIsUpdating] = useState(false);
    const [sessionId, setSessionId] = useState<string>(overrideSessionId);
    const { title, description, usageTo, sourceFormats, uuid, language, kvps, type, fileInfo, createdAt, medium, aiCompliance } =
        playlistItem;

    const isNotExpired = () => checkExpiration !== 'is-expired';

    useEffect(() => {
        setPlaylistItem(item);
        setDuration(item.duration);
        setExp(Helpers.getAssetLicenceStatus(item));
    }, [item]);

    useEffect(() => {
        setSessionId(overrideSessionId);
    }, [overrideSessionId]);

    const handleDeletionRequest = () => deleteItemRequest(item);

    const connectToMiwi = async media => {
        const type = media.type;
        media.uuid = media.medium.split('/').pop();
        const source = Helpers.getMediaSource(sourceFormats);
        const sessionTimeout = type === 'image' ? OVERRIDE_SISSION_DURATION : media.duration * 1000;
        if (overrideDeviceType === DeviceType.CHROMEBOX) {
            await setAsset2D(type, source, media)
            if(type === 'image'){
                Config.overrideSessionTimeout && clearTimeout(Config.overrideSessionTimeout);
                Config.overrideSessionTimeout = setTimeout(async() => {
                    controlMedia2D('SKIP');
                }, sessionTimeout);
            }
        } else if (overrideDeviceType === DeviceType.AVE) {
            setAsset3D(type, source, media)
        } else {
            await setOverride(media, type, source);
            Config.overrideSessionTimeout && clearTimeout(Config.overrideSessionTimeout);
            Config.overrideSessionTimeout = setTimeout(async() => {
                setOverrideMediaUrl('');
                setOverrideMedia('');
            }, sessionTimeout);
        }
    }

    const onSlideEnd = async (
        event: React.SyntheticEvent | Event,
        value: number | number[]
    ) => {
        if (value !== duration) {
            setIsEditingPlaylist(false);
            setIsUpdating(true);
            setDuration(value as number);
            const url = `${Server.baseUrl}${item[RESPONSE.ID]}`;
            const options = {
                body: {
                    duration: value
                    }
            };

            await apiPatch(url, options);

            await getPlaylist(playlistId, {
                isBackgroundRequest: true
            });

            setIsUpdating(false);
        }
    };

    const generateActionButtons = (): Array<ActionButtonType | ReactNode> =>
        [
            type === 'image' && isNotExpired() && isEditable &&(
                <div className="b-playlist-item__slider" key={uuid}>
                    <span className="b-playlist-item__slider-title">
                        {t('playlist_duration')}:
                    </span>
                    <Slider 
                        defaultValue={duration} 
                        max={60} 
                        min={5} 
                        onChangeCommitted={onSlideEnd} 
                        valueLabelDisplay="on"
                        disabled={!isEditable || !isNotExpired() || !Helpers.getMediaSource(sourceFormats)}
                        data-testid='slider'
                        valueLabelFormat={value => value + 's'}
                    />
                </div>
            ),
            {
                icon:
                    Helpers.getMediaSource(sourceFormats) && overrideMediaUrl === Helpers.getMediaSource(sourceFormats)
                        ? 'system-display-large-white'
                        : 'system-display-large',
                disabled: !overrideDeviceId || !Helpers.getMediaSource(sourceFormats) || !isNotExpired(),
                isActive: Helpers.getMediaSource(sourceFormats) && overrideMediaUrl === Helpers.getMediaSource(sourceFormats),
                onClick: () => connectToMiwi(playlistItem),
                name: `display-playlist-item-${index + 1}`
            },
            isNotExpired() && <div className="order-arrows">
            <a className={classNames('order-arrow', {'disabled': index === 0})} onClick={() => onMove(item.uuid, -1)} data-testid={`move-up-${index + 1}`}>&#x25B2;</a>
            <a className={classNames('order-arrow', {'disabled': index === itemsLength -1})} onClick={() => onMove(item.uuid, 1)} data-testid={`move-down-${index + 1}`}>&#x25BC;</a>
            </div>,
            {
                icon: 'remove',
                onClick: handleDeletionRequest,
                disabled: !isEditable,
                name: `delete-playlist-item-${index + 1}`
            }
        ].filter(Boolean);

    const itemClass = classNames('b-playlist-item', checkExpiration, {
        'is-loading': isUpdating
    });
    const validity = classNames('element-valid-until', checkExpiration);

    return (
        <div className={itemClass} key={0}>
            {isUpdating && <Loader />}
            <ItemCard
                name={`playlist-item-${index + 1}`}
                key={uuid}
                href={`/${type}s/${medium.split('/').pop()}`}
                isDisabled={disabled}
                withSortableKnob
                thumbnail={item.thumbnail}
                videoSource={type === 'video' ? Helpers.getMediaSource(sourceFormats) : ''}
                type={type}
                overlayTag={
                    <>
                        <AiComplianceIcon aiCompliance={aiCompliance} />
                        {Helpers.isRecentlyAdded(createdAt) ? (
                            <span className="overlay-tag">
                                {t('recently_added')}
                            </span>
                        ) : null}
                    </>
                }
                icon={type === 'video' ? 'media-video' : 'media-image'}
                title={title || t('no_title')}
                subtitle={
                    <div className="element-info"><span>{language ? language.toUpperCase() : 'EN'}</span>
                        <span className="element-info__duration">{` - ${Helpers.humanDuration(
                            duration
                        )} Min`}</span>
                        <span className="element-info__file-size">
                            {fileInfo ? `- ${Helpers.bytesToSize(fileInfo.fileSize)}` : ''}
                        </span>
                    </div>
                }
                description={`${description || 'no_description'}`}
                footer={<>                
                    {usageTo ? (
                        <p className={validity}>
                            {checkExpiration === 'is-expired'
                                ? `${t('playlist_not_valid_date')}:`
                                : `${t('playlist_valid_date')}:`}
                            {usageTo
                                ? ` ${Helpers.getExpirationDate(
                                      item
                                  )} ${t('hour')}`
                                : ''}
                        </p>
                    ) : (
                        <p className={validity}>
                            {t('playlist_valid_date')} : {t('no_restrictions')}
                        </p>
                    )}
                    {kvps && 
                     kvps.length > 0 &&
                        <p>
                            {`${t('dealer_content')}: `}{kvps.join(', ')}
                        </p>
                    }
                    </>
                }
                buttons={generateActionButtons()}
            />
        </div>
    );
};

export default PlaylistItem;
